import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import logoText from '@assets/logo.png';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { logoutUser, setUserDetails } from '@redux/reducers/user.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { RootState } from '@redux/store';
import { UserRoles, UserOrganizationRoleStatus, UserOrganization } from '@models/user.model';
import CreateOrganizationModal from '@components/organisms/CreateOrganizationModal';
import { useRefreshUserOrganization } from '@hooks/api/auth/useRefreshUserOrganization';
import { removeOrganizationId, setOrganizationIdLocalStorage, setOrganizationIdSessionStorage } from '@config/storage';
import userService from '@services/user';
import { OrganizationStatus } from '@models/organization.model';
import { getUserOrganizations } from '@services/currentUser';
import { Button } from '@components/atoms/Button';
import {
  SuspendedOrganizationCard,
  OwnerActions,
  NonOwnerActions,
  ReactivateSubscriptionModal,
} from './components';
import { useTranslation } from '@hooks/useTranslation';

const OrganizationSuspended: React.FC = () => {
  // Sprint 28, story-143. Bucket 2 — see docs/architecture.md "## Sprint 28" Decision 4.
  const tOrgLifecycle = useTranslation('orgLifecycle');
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [isCheckingActiveOrg, setIsCheckingActiveOrg] = useState(true);
  const [userOrganizations, setUserOrganizations] = useState<UserOrganization[]>([]);
  const [selectedOrgForReactivation, setSelectedOrgForReactivation] = useState<UserOrganization | null>(null);

  // User data from Redux
  const { email, firstName, lastName, role } = useAppSelector(
    (state: RootState) => state.user,
  );

  // Organization data from Redux
  useAppSelector((state: RootState) => state.organization);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);

  // Check if user is the owner of the suspended organization
  const isOwner = role === UserRoles.OWNER;

  // Check if user has another active organization on mount
  useEffect(() => {
    const checkForActiveOrganization = async () => {
      try {
        // Clear organizationId from storage to ensure clean state for API calls
        const currentOrgId = localStorage.getItem('organizationId') || sessionStorage.getItem('organizationId');
        if (currentOrgId) {
          removeOrganizationId();
        }

        // Make fresh request to backend without organizationId
        const data = await userService.getSelfWithOrganization();

        if (data.organization && data.user) {
          const isActive = data.user.organizationRoleStatus === UserOrganizationRoleStatus.ACTIVE;
          const orgIsEnabled = data.organization.status === OrganizationStatus.ENABLED;

          if (isActive && orgIsEnabled) {
            // User has an active organization that is ENABLED, redirect to dashboard
            dispatch(setUserDetails(data.user));
            dispatch(setOrganizationData(data.organization));
            setOrganizationIdLocalStorage(data.organization.organizationId);
            setOrganizationIdSessionStorage(data.organization.organizationId);
            history.push('/dashboard');
            return;
          }
        }

        // No active organization found, fetch all user organizations to show options
        const orgsData = await getUserOrganizations();
        if (orgsData?.getUserOrganizations) {
          setUserOrganizations(orgsData.getUserOrganizations);
        }
      } catch (error) {
        console.error('Error checking for active organization:', error);
      } finally {
        setIsCheckingActiveOrg(false);
      }
    };

    checkForActiveOrganization();
  }, [dispatch, history]);

  // Filter organizations by type
  const suspendedOrgsAsOwner = userOrganizations.filter(
    org => org.organization.status === OrganizationStatus.SUSPENDED &&
           org.role === UserRoles.OWNER &&
           org.userStatus === 'ACTIVE'
  );

  const activeOrgs = userOrganizations.filter(
    org => org.organization.status === OrganizationStatus.ENABLED &&
           org.userStatus === 'ACTIVE'
  );

  const disabledOrgs = userOrganizations.filter(
    org => org.userStatus === 'DISABLED'
  );

  // Handler to switch to an active organization
  const handleSwitchOrganization = useCallback(async (org: UserOrganization) => {
    try {
      setOrganizationIdLocalStorage(org.organizationId);
      setOrganizationIdSessionStorage(org.organizationId);
      dispatch(setOrganizationData({
        organizationId: org.organizationId,
        name: org.organization.name,
        status: org.organization.status,
      }));
      dispatch(setUserDetails({ role: org.role }));
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error switching organization:', error);
    }
  }, [dispatch]);

  // Hook for refreshing organization status
  const { mutate: refreshOrganization, isPending: isChecking } =
    useRefreshUserOrganization();

  // User data for modals
  const userData = {
    firstName: firstName || '',
    lastName: lastName || '',
    email: email || '',
  };

  // Handlers
  const handleLogout = useCallback(() => {
    dispatch(logoutUser());
    history.push('/login');
  }, [dispatch, history]);

  const handleTryAgain = useCallback(() => {
    refreshOrganization();
  }, [refreshOrganization]);

  const handleOpenCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleOpenReactivateModal = useCallback((org?: UserOrganization) => {
    if (org) {
      setSelectedOrgForReactivation(org);
    }
    setIsReactivateModalOpen(true);
  }, []);

  const handleCloseReactivateModal = useCallback(() => {
    setIsReactivateModalOpen(false);
    setSelectedOrgForReactivation(null);
  }, []);

  const handleOrganizationCreated = useCallback(() => {
    refreshOrganization();
  }, [refreshOrganization]);

  const handleReactivateSuccess = useCallback(() => {
    refreshOrganization();
  }, [refreshOrganization]);

  // Show loading while checking for active organization
  if (isCheckingActiveOrg) {
    return (
      <div className="flex flex-col gap-10 items-center justify-center min-h-screen bg-indigo-950 dark:bg-neutral-900 p-4">
        <img
          src={logoText}
          alt="Zenhammer Logo"
          className="max-w-sm w-full mb-4 md:mb-0 md:mr-6"
        />
        <div className="text-white text-lg">{tOrgLifecycle.checkingOrganizationStatus}</div>
      </div>
    );
  }

  // Get the org to use for reactivation modal
  const reactivationOrg = selectedOrgForReactivation || (suspendedOrgsAsOwner.length === 1 ? suspendedOrgsAsOwner[0] : null);

  return (
    <div className="flex flex-col gap-10 items-center justify-center min-h-screen bg-indigo-950 dark:bg-neutral-900 p-4">
      <img
        src={logoText}
        alt="Zenhammer Logo"
        className="max-w-sm w-full mb-4 md:mb-0 md:mr-6"
      />

      <SuspendedOrganizationCard>
        {/* Show active organizations if available */}
        {activeOrgs.length > 0 && (
          <div className="w-full mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <h3 className="text-sm font-semibold text-green-800 dark:text-green-200 mb-3">
              {tOrgLifecycle.activeOrganizationsHeading}
            </h3>
            <div className="space-y-2">
              {activeOrgs.map((org) => (
                <div key={org.organizationId} className="flex items-center justify-between gap-3 p-3 bg-white dark:bg-gray-700 rounded-md">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0">
                    <span className="font-medium text-gray-900 dark:text-white truncate">{org.organization.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">({org.role})</span>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => handleSwitchOrganization(org)}
                    className="shrink-0"
                  >
                    {tOrgLifecycle.switchOrganization}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Show suspended organizations where user is owner */}
        {suspendedOrgsAsOwner.length > 0 && (
          <div className="w-full mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-3">
              {tOrgLifecycle.suspendedOrganizationsHeading}
            </h3>
            <div className="space-y-2">
              {suspendedOrgsAsOwner.map((org) => (
                <div key={org.organizationId} className="flex items-center justify-between gap-3 p-3 bg-white dark:bg-gray-700 rounded-md">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0">
                    <span className="font-medium text-gray-900 dark:text-white truncate">{org.organization.name}</span>
                    <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-200 rounded-full">
                      {tOrgLifecycle.suspendedBadge}
                    </span>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => handleOpenReactivateModal(org)}
                    className="shrink-0"
                  >
                    {tOrgLifecycle.reactivate}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Show organizations where user is disabled */}
        {disabledOrgs.length > 0 && (
          <div className="w-full mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-3">
              {tOrgLifecycle.disabledOrganizationsHeading}
            </h3>
            <div className="space-y-2">
              {disabledOrgs.map((org) => (
                <div key={org.organizationId} className="flex items-center justify-between gap-3 p-3 bg-white dark:bg-gray-700 rounded-md">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0">
                    <span className="font-medium text-gray-900 dark:text-white truncate">{org.organization.name}</span>
                    <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 rounded-full">
                      {tOrgLifecycle.disabledBadge}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{tOrgLifecycle.contactAdmin}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Default actions */}
        <div className="w-full pt-2">
          {isOwner && suspendedOrgsAsOwner.length === 0 ? (
            <OwnerActions
              onReactivate={() => handleOpenReactivateModal()}
              onCreateOrganization={handleOpenCreateModal}
              onTryAgain={handleTryAgain}
              onLogout={handleLogout}
              isChecking={isChecking}
            />
          ) : !isOwner || suspendedOrgsAsOwner.length === 0 ? (
            <NonOwnerActions
              onCreateOrganization={handleOpenCreateModal}
              onTryAgain={handleTryAgain}
              onLogout={handleLogout}
              isChecking={isChecking}
            />
          ) : (
            <div className="flex flex-col gap-3 w-full">
              <Button variant="primary" onClick={handleOpenCreateModal} className="w-full">
                {tOrgLifecycle.createNewOrganization}
              </Button>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={handleTryAgain} disabled={isChecking} className="flex-1">
                  {isChecking ? tOrgLifecycle.checking : tOrgLifecycle.tryAgain}
                </Button>
                <Button variant="secondary" onClick={handleLogout} className="flex-1">
                  {tOrgLifecycle.logout}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SuspendedOrganizationCard>

      <CreateOrganizationModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSuccess={handleOrganizationCreated}
        userData={userData}
      />

      {reactivationOrg && (
        <ReactivateSubscriptionModal
          isOpen={isReactivateModalOpen}
          onClose={handleCloseReactivateModal}
          onSuccess={handleReactivateSuccess}
          organizationId={reactivationOrg.organizationId}
          organizationName={reactivationOrg.organization.name}
          userData={userData}
        />
      )}
    </div>
  );
};

export default OrganizationSuspended;
